import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { formatCurrency } from '@utils/formatters';
import { formatCompact, MONTHS_SHORT, CAT_COLORS } from './dashboardHelpers';
import ChartTooltip from './ChartTooltip';

const DashboardCharts = ({ balanceData, topExpenseCategories, categoryTotal }) => {
  return (
    <div className="grid lg:grid-cols-5 gap-4">
      {/* Balance Evolution */}
      <div className="lg:col-span-3 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">Evolução Patrimonial</h3>
            <p className="text-xs text-gray-400 mt-0.5">Últimos 6 meses</p>
          </div>
        </div>
        {balanceData.length > 1 ? (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={balanceData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="balGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="incGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" className="dark:stroke-gray-700" />
              <XAxis dataKey="month"
                tickFormatter={m => { const [,mo] = m.split('-'); return MONTHS_SHORT[parseInt(mo)-1]; }}
                stroke="#9ca3af" fontSize={11} />
              <YAxis tickFormatter={v => `${(v/1000).toFixed(0)}k`} stroke="#9ca3af" fontSize={11} width={40} />
              <Tooltip content={<ChartTooltip formatter={formatCurrency} />}
                labelFormatter={m => { const [y,mo] = m.split('-'); return `${MONTHS_SHORT[parseInt(mo)-1]}/${y.slice(2)}`; }} />
              <Area type="monotone" dataKey="income" name="Receitas" stroke="#22c55e" strokeWidth={1.5}
                fill="url(#incGrad)" dot={false} />
              <Area type="monotone" dataKey="balance" name="Patrimônio" stroke="#0ea5e9" strokeWidth={2.5}
                fill="url(#balGrad)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-56 flex items-center justify-center text-gray-400 text-sm">
            Sem dados suficientes
          </div>
        )}
      </div>

      {/* Top Categorias */}
      <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">Top Gastos</h3>
            <p className="text-xs text-gray-400 mt-0.5">Por categoria no período</p>
          </div>
          {categoryTotal > 0 && (
            <span className="text-xs text-red-600 font-medium bg-red-50 dark:bg-red-900/20 px-2 py-0.5 rounded-full">
              {formatCompact(categoryTotal)}
            </span>
          )}
        </div>
        {topExpenseCategories.length > 0 ? (
          <div className="space-y-3">
            {topExpenseCategories.map((cat, i) => (
              <div key={cat.categoryId}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="text-base">{cat.icon}</span>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate max-w-[110px]">
                      {cat.name}
                    </span>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                      {formatCompact(cat.total)}
                    </span>
                    <span className="text-xs text-gray-400 ml-1">{cat.percentage?.toFixed(0)}%</span>
                  </div>
                </div>
                <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${cat.percentage}%`, backgroundColor: CAT_COLORS[i % CAT_COLORS.length] }}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="h-40 flex items-center justify-center text-gray-400 text-sm">
            Sem gastos no período
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardCharts;
